import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Alert, Button, Form, Placeholder } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { crmPostventaV2Api } from "../../crmpostventa-v2/crmPostventaV2Api";
import AlertFilterDropdown from "../AlertFilterDropdown";
import AlertScrollArea from "../AlertScrollArea";
import ZocoCheckAlertsActionsMenu from "./ZocoCheckAlertsActionsMenu";
import {
  buildAttemptLabel,
  buildFilterOptions,
  buildWhatsAppUrl,
  DEFAULT_ZOCO_CHECK_PAGE_SIZE,
  filterZocoCheckRows,
  formatDate,
  mapStateLabel,
  normalizeText,
  normalizeZocoCheckItem,
  slicePage,
  ZOCO_CHECK_PAGE_SIZE_OPTIONS,
} from "./zocoCheckAlertsUtils";
import "./zoco-check-alerts.css";

const SEARCH_DEBOUNCE_MS = 300;
const FETCH_PAGE_SIZE = 200;
const ALERTS_VISIBLE_ROWS = 5;
const MOBILE_BREAKPOINT = 900;
const makeToday = () => new Date().toISOString().slice(0, 10);

const PRIORITY_CLASS_BY_VALUE = {
  CRITICA: "is-critical",
  ALTA: "is-high",
  MEDIA: "is-medium",
  BAJA: "is-low",
};

const PRIORITY_LABELS = {
  CRITICA: "Crítica",
  ALTA: "Alta",
  MEDIA: "Media",
  BAJA: "Baja",
};

const emptyFilters = {
  search: "",
  priority: "",
  province: "",
  responsible: "",
  status: "",
  attempt: "",
};

const createGenerationSummary = (response, estimatedVisible) => [
  response?.message || "Sin mensaje.",
  Number.isFinite(response?.elegibles) ? `Elegibles: ${response.elegibles}` : null,
  Number.isFinite(response?.crear) ? `A crear en esta corrida: ${response.crear}` : null,
  Number.isFinite(response?.pendientesPorLimite) ? `Pendientes: ${response.pendientesPorLimite}` : null,
  estimatedVisible > 0 ? `Estimación visual con filtros actuales: ${estimatedVisible}` : null,
]
  .filter(Boolean)
  .join("\n");

const buildEmptyMessage = (hasFilters, customEmptyMessage) =>
  hasFilters
    ? "No se encontraron seguimientos con los filtros seleccionados."
    : customEmptyMessage || "No hay seguimientos Zoco Check pendientes.";

export default function ZocoCheckAlertsSection({
  processCode,
  processSlug,
  title,
  description = "Seguimiento semanal por comercio derivado desde onboarding.",
  globalFilters,
  emptyMessage,
  onManage,
  onAssign,
  onRelease,
  onTake,
  onRefreshSummary,
  onMutationNonce = 0,
  isManagementView = false,
}) {
  const [filters, setFilters] = useState(emptyFilters);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [state, setState] = useState({
    loading: true,
    rows: [],
    total: 0,
    error: "",
    permissions: null,
  });
  const [rowBusy, setRowBusy] = useState({});
  const [detailsCache, setDetailsCache] = useState({});
  const [generateBusy, setGenerateBusy] = useState(false);
  const [headerDate, setHeaderDate] = useState(globalFilters?.fechaReferencia || makeToday());
  const [viewportMobile, setViewportMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );

  const selectedHeaderDate = useMemo(() => {
    if (!headerDate) return new Date();
    const parsed = new Date(`${headerDate}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }, [headerDate]);

  useEffect(() => {
    setHeaderDate(globalFilters?.fechaReferencia || makeToday());
  }, [globalFilters?.fechaReferencia]);

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((value) => Boolean(normalizeText(value))),
    [filters]
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(filters.search), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [filters.search]);

  useEffect(() => {
    const onResize = () => setViewportMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);



  const loadRows = async () => {
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      let pageNumber = 1;
      let mergedRows = [];
      let total = 0;

      while (pageNumber === 1 || mergedRows.length < total) {
        const response = await crmPostventaV2Api.obtenerAlertas({
          procesoCodigo: processCode,
          fechaReferencia: globalFilters?.fechaReferencia,
          prioridad: globalFilters?.prioridad || undefined,
          estado: globalFilters?.estado || undefined,
          texto: globalFilters?.texto || undefined,
          pagina: pageNumber,
          tamanoPagina: FETCH_PAGE_SIZE,
        });

        const currentRows = Array.isArray(response?.filas) ? response.filas : [];
        total = Number(response?.total || currentRows.length || 0);
        mergedRows = mergedRows.concat(currentRows);

        if (!currentRows.length || mergedRows.length >= total) {
          setState({
            loading: false,
            rows: mergedRows.map((item) => normalizeZocoCheckItem({ ...item, procesoSlug: processSlug })),
            total,
            permissions: response?.permisos || null,
            error: "",
          });
          return;
        }

        pageNumber += 1;
      }
    } catch (error) {
      setState({
        loading: false,
        rows: [],
        total: 0,
        permissions: null,
        error: "No pudimos cargar los seguimientos Zoco Check.",
      });
    }
  };

  useEffect(() => {
    loadRows();
  }, [globalFilters?.estado, globalFilters?.fechaReferencia, globalFilters?.prioridad, globalFilters?.texto, processCode, processSlug]);

  useEffect(() => {
    if (onMutationNonce > 0) {
      loadRows();
    }
  }, [onMutationNonce]);

  const filteredRows = useMemo(
    () => filterZocoCheckRows(state.rows, { ...filters, search: debouncedSearch }),
    [debouncedSearch, filters, state.rows]
  );
  const visibleRows = isManagementView ? filteredRows : filteredRows.slice(0, ALERTS_VISIBLE_ROWS);

  const priorityOptions = useMemo(() => buildFilterOptions(state.rows, (item) => item.priority), [state.rows]);
  const provinceOptions = useMemo(() => buildFilterOptions(state.rows, (item) => item.province), [state.rows]);
  const responsibleOptions = useMemo(() => buildFilterOptions(state.rows, (item) => item.responsible), [state.rows]);
  const statusOptions = useMemo(() => buildFilterOptions(state.rows, (item) => item.statusLabel), [state.rows]);
  const attemptOptions = useMemo(
    () => buildFilterOptions(state.rows, (item) => String(item.attempt || "")),
    [state.rows]
  );

  const canGenerate = false;

  const readDetail = async (item) => {
    const cacheKey = String(item.id);
    if (detailsCache[cacheKey]) {
      return detailsCache[cacheKey];
    }

    const detail = await crmPostventaV2Api.obtenerDetalle(item.processSlug || processSlug, item.id);
    setDetailsCache((prev) => ({ ...prev, [cacheKey]: detail }));
    return detail;
  };

  useEffect(() => {
    const missingRows = filteredRows.filter((item) => !detailsCache[String(item.id)]);
    if (!missingRows.length) {
      return;
    }

    Promise.allSettled(missingRows.map((item) => readDetail(item))).catch(() => null);
  }, [filteredRows, detailsCache]);

  const handleResultChange = async (item, nextResult) => {
    if (!nextResult) {
      return;
    }

    setRowBusy((prev) => ({ ...prev, [item.id]: "result" }));
    try {
      await readDetail(item);
      await onManage?.(item, { initialResult: nextResult });
    } catch (error) {
      const message = String(error?.message || "");
      if (message.includes("403")) {
        Swal.fire(title, "No tenés permisos para modificar este seguimiento.", "warning");
      } else if (message.includes("409")) {
        Swal.fire(title, "Este seguimiento fue actualizado por otro usuario. Actualizamos la información para que puedas continuar.", "info");
        await loadRows();
      } else {
        Swal.fire(title, message || "No se pudo preparar la gestión.", "error");
      }
    } finally {
      setRowBusy((prev) => ({ ...prev, [item.id]: "" }));
    }
  };

  const handleCopyMessage = async (message) => {
    try {
      await navigator.clipboard.writeText(message || "");
      Swal.fire(title, "El mensaje sugerido quedó copiado.", "success");
    } catch {
      Swal.fire(title, "No se pudo copiar el mensaje sugerido.", "error");
    }
  };

  const handleGenerate = async () => {
    setGenerateBusy(true);
    try {
      const preview = await crmPostventaV2Api.generar(processSlug, {
        dryRun: true,
        fecha: globalFilters?.fechaReferencia,
      });

      const confirmation = await Swal.fire({
        title: "Generar Zoco Check",
        text: createGenerationSummary(preview, filteredRows.length),
        html: `<pre style="text-align:left;white-space:pre-wrap;margin:0;">${createGenerationSummary(preview, filteredRows.length)}</pre><p style="margin-top:16px;">Se generarán los seguimientos Zoco Check correspondientes a los comercios que cumplen las condiciones actuales. Esta acción puede crear nuevas gestiones. ¿Deseás continuar?</p>`,
        icon: preview?.permiteEjecucionReal === false ? "warning" : "question",
        showCancelButton: true,
        confirmButtonText: "Generar Zoco Check",
        cancelButtonText: "Cancelar",
      });

      if (!confirmation.isConfirmed || preview?.permiteEjecucionReal === false) {
        return;
      }

      const response = await crmPostventaV2Api.generar(processSlug, {
        dryRun: false,
        fecha: globalFilters?.fechaReferencia,
      });

      await Swal.fire(title, response?.message || "Generación completada.", "success");
      await loadRows();
      await onRefreshSummary?.();
    } catch (error) {
      Swal.fire(title, error?.message || "No se pudo generar Zoco Check.", "error");
    } finally {
      setGenerateBusy(false);
    }
  };

  const resetFilters = () => {
    setFilters((prev) => ({
      ...emptyFilters,
      search: "",
      priority: "",
      province: "",
      responsible: "",
      status: "",
      attempt: "",
    }));
  };

  const renderRowActions = (item) => {
    const whatsAppUrl = buildWhatsAppUrl(item.phone, item.suggestedMessage);
    const isBusy = Boolean(rowBusy[item.id]);

    return (
      <div className="zoco-check-alerts__row-actions">
        <Button
          type="button"
          size="sm"
          variant="light"
          className="zoco-check-alerts__details-btn"
          onClick={() => onManage?.(item)}
          disabled={isBusy}
        >
          Ver detalles
        </Button>
        <Button
          type="button"
          size="sm"
          className="zoco-check-alerts__whatsapp-btn"
          href={whatsAppUrl || undefined}
          target={whatsAppUrl ? "_blank" : undefined}
          rel={whatsAppUrl ? "noopener noreferrer" : undefined}
          disabled={!whatsAppUrl || isBusy}
          aria-label={whatsAppUrl ? `Abrir WhatsApp para ${item.commerceName}` : "El comercio no tiene teléfono cargado"}
          title={whatsAppUrl ? "Abrir WhatsApp" : "El comercio no tiene teléfono cargado"}
        >
          <FontAwesomeIcon icon={faWhatsapp} />
          <span>Whatsapp</span>
        </Button>
        <ZocoCheckAlertsActionsMenu
          item={item}
          disabled={isBusy}
          onTake={onTake}
          onAssign={onAssign}
          onRelease={onRelease}
          onManage={onManage}
          onCopyMessage={handleCopyMessage}
        />
      </div>
    );
  };

  const renderDesktopList = () => (
    <div className="zoco-check-alerts__table-shell">
      <div className="zoco-check-alerts__table-head">
        <span>Comercio</span>
        <span>Seguimiento</span>
        <span>Anticipo</span>
        <span>Prioridad</span>
        <span>Responsable</span>
        <span>Próx. acción</span>
        <span>Acción</span>
      </div>

      <AlertScrollArea viewportClassName={`zoco-check-alerts__list ${isManagementView ? "is-management-view" : ""}`}>
        {visibleRows.map((item) => {
          const priorityKey = normalizeText(item.priority).toUpperCase();
          const priorityClassName = PRIORITY_CLASS_BY_VALUE[priorityKey] || "is-neutral";
          const hasResponsible = Boolean(item.responsible) && item.responsible !== "Sin asignar";

          return (
            <article key={item.id} className="zoco-check-alert-card">
              <div className="zoco-check-alert-card__commerce">
                <strong>{item.commerceName}</strong>
                <div className="zoco-check-alert-card__meta">
                  <span><b>CUIT:</b> {item.cuit}</span>
                  {item.terminal ? <span><b>Terminal:</b> {item.terminal}</span> : null}
                  {item.province ? <span><b>Provincia:</b> {item.province}</span> : null}
                </div>
              </div>

              <div className="zoco-check-alert-card__tracking">
                <span className="zoco-check-alert-card__pill">{item.statusLabel}</span>
                <div className="zoco-check-alert-card__meta is-inline">
                  {item.channel ? <span><b>Canal:</b> {item.channel}</span> : null}
                  <span><b>Intento:</b> {buildAttemptLabel(item.attempt, item.maxAttempts)}</span>
                </div>
              </div>

              <div className="zoco-check-alert-card__advance">
                {item.advanceActive === null ? (
                  <span className="zoco-check-alert-card__empty">Sin dato</span>
                ) : (
                  <span className={`zoco-check-alert-card__advance-pill ${item.advanceActive ? "is-active" : "is-inactive"}`}>
                    <span className="zoco-check-alert-card__dot" aria-hidden="true" />
                    {item.advanceActive ? "Activo" : "Inactivo"}
                  </span>
                )}
              </div>

              <div className="zoco-check-alert-card__priority">
                {item.priority ? (
                  <span className={`zoco-check-alert-card__priority-pill ${priorityClassName}`}>
                    {PRIORITY_LABELS[priorityKey] || item.priority}
                  </span>
                ) : (
                  <span className="zoco-check-alert-card__priority-pill is-empty">Sin prioridad</span>
                )}
              </div>

              <div className="zoco-check-alert-card__responsible">
                <span className={hasResponsible ? "" : "is-empty"}>{item.responsible}</span>
              </div>

              <div className="zoco-check-alert-card__next-action">
                {item.action ? <strong>{item.action}</strong> : <span>Sin próxima acción</span>}
              </div>

              <div className="zoco-check-alert-card__actions">
                <Button
                  type="button"
                  className="zoco-check-alert-card__manage-btn"
                  onClick={() => onManage?.(item)}
                  disabled={Boolean(rowBusy[item.id])}
                >
                  Gestionar
                </Button>
              </div>
            </article>
          );
        })}
      </AlertScrollArea>
    </div>
  );

  const renderMobileCards = () => (
    <AlertScrollArea viewportClassName={`zoco-check-alerts__cards ${isManagementView ? "is-management-view" : ""}`}>
      {visibleRows.map((item) => {
        const cachedDetail = detailsCache[String(item.id)];
        const resultOptions = Array.isArray(cachedDetail?.instancia?.resultadosPermitidos)
          ? cachedDetail.instancia.resultadosPermitidos
          : [];
        const advanceLabel = item.advanceActive === null ? "Sin dato" : item.advanceActive ? "Activo" : "Inactivo";

        return (
          <article key={item.id} className="zoco-check-alerts__card">
            <div className="zoco-check-alerts__card-head">
              <strong>{item.commerceName}</strong>
              <span>{item.cuit}</span>
            </div>
            <div className="zoco-check-alerts__card-grid">
              <div>
                <span>Anticipo</span>
                <strong>
                  <span className={`zoco-check-alerts__advance ${item.advanceActive ? "is-active" : "is-inactive"}`}>
                    {advanceLabel}
                  </span>
                </strong>
              </div>
              <div>
                <span>Intento</span>
                <strong>{buildAttemptLabel(item.attempt, item.maxAttempts)}</strong>
              </div>
              <div>
                <span>Estado</span>
                <strong>{item.statusLabel}</strong>
              </div>
              <div>
                <span>Próx. contacto</span>
                <strong>{formatDate(item.nextContactAt)}</strong>
              </div>
              <div>
                <span>Resultado</span>
                <Form.Select
                  size="sm"
                  className="zoco-check-alerts__mini-select"
                  value=""
                  onFocus={() => readDetail(item).catch(() => null)}
                  onChange={(event) => handleResultChange(item, event.target.value)}
                  disabled={item.isReadOnly || rowBusy[item.id] === "result"}
                >
                  <option value="">Seleccionar</option>
                  {resultOptions.map((option) => (
                    <option key={`${item.id}-mobile-${option}`} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
            {renderRowActions(item)}
          </article>
        );
      })}
    </AlertScrollArea>
  );

  return (
    <section className={`zoco-check-alerts-section ${isManagementView ? "is-management-view" : "is-alert-view"}`}>
      <div className="zoco-check-alerts__header-card">
        <div className="zoco-check-alerts__header-copy">
          <h3 className="zoco-check-alerts__header-title">{title}</h3>
          <p className="zoco-check-alerts__header-description">{description}</p>
        </div>

        <div className="zoco-check-alerts__date-selector">
          <DatePicker
            selected={selectedHeaderDate}
            onChange={(date) => setHeaderDate(date ? date.toISOString().slice(0, 10) : makeToday())}
            dateFormat="yyyy-MM-dd"
            className="zoco-check-alerts__date-input"
            calendarClassName="zoco-check-alerts__datepicker-calendar"
            popperClassName="zoco-check-alerts__datepicker-popper"
            ariaLabelledBy="zoco-check-date-label"
            placeholderText="Seleccionar fecha"
          />
          <FontAwesomeIcon icon={faCalendar} className="zoco-check-alerts__date-icon" />
        </div>
      </div>

      <div className="zoco-check-alerts__header">
        <span className="crm-alert-section-count">| Mostrando <strong>{visibleRows.length}</strong> registros</span>
      </div>

      <div className="crm-alert-filters zoco-check-alerts__filters">
        <div className="zoco-check-alerts__filters-controls">
          <div className="crm-alert-filters__search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <Form.Control
              type="search"
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              placeholder="Comercio, CUIT, n.º..."
              aria-label="Buscar seguimientos Zoco Check"
            />
          </div>
          <AlertFilterDropdown value={filters.priority} options={[{ value: "", label: "Prioridad" }, ...priorityOptions.map((item) => ({ value: item, label: item }))]} onChange={(value) => setFilters((prev) => ({ ...prev, priority: value }))} ariaLabel="Filtrar por prioridad" />
          <AlertFilterDropdown value={filters.province} options={[{ value: "", label: "Provincia" }, ...provinceOptions.map((item) => ({ value: item, label: item }))]} onChange={(value) => setFilters((prev) => ({ ...prev, province: value }))} ariaLabel="Filtrar por provincia" />
          <AlertFilterDropdown value={filters.responsible} options={[{ value: "", label: "Responsable" }, ...responsibleOptions.map((item) => ({ value: item, label: item }))]} onChange={(value) => setFilters((prev) => ({ ...prev, responsible: value }))} ariaLabel="Filtrar por responsable" />
          <AlertFilterDropdown value={filters.status} options={[{ value: "", label: "Estado" }, ...statusOptions.map((item) => ({ value: item, label: item }))]} onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))} ariaLabel="Filtrar por estado" />
          <AlertFilterDropdown value={filters.attempt} options={[{ value: "", label: "Intento" }, ...attemptOptions.map((item) => ({ value: item, label: item }))]} onChange={(value) => setFilters((prev) => ({ ...prev, attempt: value }))} ariaLabel="Filtrar por intento" />
        </div>
        <Button variant="light" size="sm" onClick={resetFilters} disabled={!hasActiveFilters} className="zoco-check-alerts__filter-clear-btn">
          Limpiar filtros
        </Button>
      </div>

      {state.loading ? (
        <div className="zoco-check-alerts__skeleton">
          <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
          <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
          <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
        </div>
      ) : state.error ? (
        <div className="crm-alert-load-error" role="alert">
          <span className="crm-alert-load-error__icon" aria-hidden="true">!</span>
          <span>No pudimos cargar los procesos de Zoco Check.</span>
        </div>
      ) : !filteredRows.length ? (
        hasActiveFilters ? (
          <Alert variant="secondary" className="mb-0">
            {buildEmptyMessage(true, emptyMessage)}
          </Alert>
        ) : (
          <div className="crm-alert-load-error" role="status">
            <span className="crm-alert-load-error__icon" aria-hidden="true">!</span>
            <span>{buildEmptyMessage(false, emptyMessage)}</span>
          </div>
        )
      ) : viewportMobile ? (
        renderMobileCards()
      ) : (
        renderDesktopList()
      )}

      <div className="zoco-check-alerts__footer">
        <div className="zoco-check-alerts__footer-actions">
          {canGenerate ? (
            <Button variant="success" onClick={handleGenerate} disabled={generateBusy}>
              {generateBusy ? "Generando..." : "Generar Zoco Check"}
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
